/**
 * PlannedTaskCard Component
 *
 * 顯示計劃任務的卡片，支持：
 * - 桌面版：拖曳到日曆創建 TimeBox
 * - 行動版：點擊進入放置模式（選取 -> 放置 -> 確認）
 */
import React, { useCallback, useRef, useEffect, useState } from 'react';
import { Task } from '../../../types/task';
import { formatPlanForDisplay } from '../../../utils/dateRangeUtils';
import { getCategoryStyle, getPriorityLabel } from '../../../config/taskStyles';
import { Clock, Calendar, GripVertical } from 'lucide-react';
import { eventBus, EVENTS } from '../../../utils/eventBus';

export interface PlannedTaskCardProps {
  task: Task;
  /** 是否可拖曳（桌面版） */
  draggable?: boolean;
  /** 拖曳開始回調（滑鼠） */
  onDragStart?: (task: Task, e: React.DragEvent) => void;
  /** 拖曳結束回調 */
  onDragEnd?: () => void;
  /** 觸控拖曳開始回調（平板） */
  onTouchStart?: (task: Task, e: React.TouchEvent) => void;
  /** 點擊排程回調（行動版 - 舊版快速排程） */
  onQuickSchedule?: (task: Task) => void;
  /** 進入放置模式回調（行動版 - 新版互動） */
  onEnterPlacementMode?: (task: Task) => void;
  /** 點擊查看詳情 */
  onViewDetail?: (task: Task) => void;
  /** 是否顯示緊湊模式 */
  compact?: boolean;
  /** 是否高亮顯示（從 TaskDetail Schedule 跳轉而來） */
  isHighlighted?: boolean;
  /** 清除高亮回調 */
  onClearHighlight?: () => void;
}

/**
 * 格式化預估時間顯示
 */
function formatEstimatedTime(minutes: number | undefined): string {
  if (!minutes) return '';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

const PlannedTaskCard: React.FC<PlannedTaskCardProps> = ({
  task,
  draggable = false,
  onDragStart,
  onDragEnd,
  onTouchStart,
  onQuickSchedule,
  onEnterPlacementMode,
  onViewDetail,
  compact = false,
  isHighlighted = false,
  onClearHighlight,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  // 高亮時自動滾動到可見區域，並播放入場動畫
  useEffect(() => {
    if (isHighlighted && cardRef.current) {
      setIsAnimating(true);
      requestAnimationFrame(() => {
        cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      // 動畫結束後切換到穩定高亮
      const timer = setTimeout(() => setIsAnimating(false), 1500);
      return () => clearTimeout(timer);
    }
  }, [isHighlighted]);
  // 處理拖曳開始
  const handleDragStart = useCallback(
    (e: React.DragEvent) => {
      if (!draggable) return;

      // 設置拖曳數據
      e.dataTransfer.setData('application/json', JSON.stringify({
        type: 'planned-task',
        taskId: task.id,
        estimatedTime: task.estimatedTime || 60,
      }));
      e.dataTransfer.effectAllowed = 'copy';

      // 用戶操作此 Task，清除高亮
      if (isHighlighted) onClearHighlight?.();

      // 回調
      onDragStart?.(task, e);
    },
    [task, draggable, onDragStart, isHighlighted, onClearHighlight]
  );

  // 處理拖曳結束
  const handleDragEnd = useCallback(() => {
    onDragEnd?.();
  }, [onDragEnd]);

  // 處理觸控開始（平板拖曳）
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!draggable || !onTouchStart) return;

      // 只處理單點觸控
      if (e.touches.length !== 1) return;

      // 阻止默認行為避免滾動
      e.preventDefault();
      e.stopPropagation();

      // 回調
      onTouchStart(task, e);
    },
    [task, draggable, onTouchStart]
  );

  // 處理點擊
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      // 用戶操作此 Task，清除高亮
      if (isHighlighted) onClearHighlight?.();

      // 優先使用新的放置模式（如果提供）
      if (onEnterPlacementMode) {
        // 發佈 UI 事件用於 Tip 自動標記
        eventBus.publish(EVENTS.UI.TASK_SCHEDULED_FROM_LAYER);
        onEnterPlacementMode(task);
      } else if (onQuickSchedule) {
        // 回退到舊版快速排程
        onQuickSchedule(task);
      } else if (onViewDetail) {
        onViewDetail(task);
      }
    },
    [task, onEnterPlacementMode, onQuickSchedule, onViewDetail, isHighlighted, onClearHighlight]
  );

  // 獲取類別樣式
  const categoryBadgeStyle = getCategoryStyle(task.category, 'badge');

  // Plan 日期顯示
  const planDisplay = task.plan ? formatPlanForDisplay(task.plan) : null;

  // 是否過期
  const isOverdue = planDisplay?.includes('ago') || planDisplay === 'Yesterday';

  return (
    <div
      ref={cardRef}
      data-testid={`planned-task-card-${task.id}`}
      className={`
        group relative rounded-lg
        transition-all duration-200
        ${isHighlighted
          ? `ring-1 ring-sky-300 bg-sky-50/40 shadow-sm border border-sky-100 ${isAnimating ? 'animate-highlight-pulse' : ''}`
          : 'bg-white border border-gray-200 hover:shadow-md hover:border-gray-300'
        }
        ${draggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}
        ${compact ? 'p-2' : 'p-3'}
      `}
      draggable={draggable}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onTouchStart={handleTouchStart}
      onClick={handleClick}
    >
      {/* 拖曳手柄 - 永遠顯示，compact 模式下圖示較小 */}
      {draggable && (
        <div className="absolute left-0.5 top-1/2 -translate-y-1/2 opacity-40">
          <GripVertical className={compact ? 'w-3 h-3' : 'w-4 h-4'} strokeWidth={2.5} color="#9ca3af" />
        </div>
      )}

      {/* 主內容區 - compact 模式下左邊距較小 */}
      <div className={draggable ? (compact ? 'pl-3' : 'pl-4') : ''}>
        {/* 標題 */}
        <div className={`font-medium text-gray-900 ${compact ? 'text-xs' : 'text-sm'} line-clamp-2`}>
          {task.title}
        </div>

        {/* 元資訊行 */}
        <div className={`flex items-center gap-2 flex-wrap ${compact ? 'mt-1' : 'mt-2'}`}>
          {/* 預估時間 */}
          {task.estimatedTime && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-500">
              <Clock className="w-3 h-3" />
              {formatEstimatedTime(task.estimatedTime)}
            </span>
          )}

          {/* Plan 日期標籤 */}
          {planDisplay && (
            <span
              className={`
                inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded
                ${isOverdue
                  ? 'bg-red-100 text-red-700'
                  : planDisplay === 'Today'
                    ? 'bg-sky-100 text-sky-700'
                    : 'bg-gray-100 text-gray-600'
                }
              `}
            >
              <Calendar className="w-3 h-3" />
              {planDisplay}
            </span>
          )}

          {/* 類別標籤 */}
          {task.category && !compact && (
            <span className={`text-xs px-1.5 py-0.5 rounded ${categoryBadgeStyle}`}>
              {task.category}
            </span>
          )}

          {/* 優先級標籤（高優先級才顯示） */}
          {task.priority === '1' && !compact && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700">
              {getPriorityLabel(task.priority)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlannedTaskCard;
